/**
 * Reports a cold load that is still showing the loading overlay long after it
 * should have rendered, so we can measure how often students hit the stall
 * tracked in [#3697](https://github.com/CodeinPlace/cip/issues/3697).
 *
 * Two lines share one prefix, so a single Cloud Logging filter on
 * `[slow-cold-load]` finds both and `event=` splits them:
 *
 * - `event=tripped` — the overlay was still up at the threshold. This is the
 *   rate: what share of loads stall at all.
 * - `event=rendered` — a load that tripped eventually rendered, with the real
 *   elapsed time. This is the duration, and it is the only one of the two that
 *   moves if the long-poll timeout cap shortens the stall.
 *
 * Reporting goes through `console.warn`, which the production block in
 * `index.tsx` forwards to the `logWarning` callable with the url, session id,
 * client commit and user agent already attached. That callable travels over
 * HTTPS rather than the Firestore channel it is reporting on, so the report can
 * still escape a page whose Firestore connection is the thing that is stuck.
 */

import {isGlobalLoadingVisible} from 'contexts/LoadingContext';
import {readNavigationType, readWasDiscarded} from './pageLoadReport';

/**
 * How long a cold load may sit on the loading overlay before it is reported.
 *
 * Well above a healthy load, which renders in about 2s. Kept below 10s because
 * capping Firestore's long-poll timeout at 10s is the mitigation under
 * consideration, and a threshold sitting on the capped stall would race it.
 */
const SLOW_COLD_LOAD_THRESHOLD_MS = 8_000;

/** How often to check whether a load that tripped has finally rendered. */
const RENDER_POLL_INTERVAL_MS = 250;

/**
 * When to give up waiting for a tripped load to render. Past this the student
 * has almost certainly left, and their `event=tripped` line already counted
 * them.
 */
const RENDER_WAIT_TIMEOUT_MS = 60_000;

/**
 * Whether this tab has been backgrounded at any point. Browsers throttle timers
 * in a hidden tab, so an elapsed time measured across one is not comparable to
 * one measured on a tab someone was actually watching.
 */
let tabHasBeenHidden = false;

/**
 * Schedules a one-shot check for a cold load that never finished rendering.
 *
 * The threshold report fires as soon as the threshold is crossed rather than
 * waiting for the render, so a student who gives up and closes the tab is still
 * counted.
 *
 * @return Nothing; any report is emitted later, from a timer.
 */
export function watchForSlowColdLoad(): void {
  tabHasBeenHidden = document.visibilityState === 'hidden';
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      tabHasBeenHidden = true;
    }
  });

  window.setTimeout(() => {
    if (!isGlobalLoadingVisible() || tabHasBeenHidden) {
      return;
    }

    // `performance.now()` is measured from navigation start, and is logged
    // instead of the threshold because timer scheduling can push the real
    // figure past it.
    console.warn(
      `[slow-cold-load] event=tripped ` +
        `elapsedMs=${Math.round(performance.now())} ` +
        `thresholdMs=${SLOW_COLD_LOAD_THRESHOLD_MS} ` +
        `longPolling=${String(window.__cipFirestoreLongPolling)} ` +
        `navigation=${readNavigationType()} ` +
        `wasDiscarded=${String(readWasDiscarded())}`,
    );
    reportWhenRendered();
  }, SLOW_COLD_LOAD_THRESHOLD_MS);
}

/** Polls until the overlay comes down, then reports how long the load took. */
function reportWhenRendered(): void {
  const deadline = performance.now() + RENDER_WAIT_TIMEOUT_MS;

  const poll = window.setInterval(() => {
    const isTimedOut = performance.now() >= deadline;
    if (isGlobalLoadingVisible() && !isTimedOut) {
      return;
    }

    window.clearInterval(poll);
    if (isTimedOut || tabHasBeenHidden) {
      return;
    }
    console.warn(
      `[slow-cold-load] event=rendered ` +
        `elapsedMs=${Math.round(performance.now())} ` +
        `longPolling=${String(window.__cipFirestoreLongPolling)}`,
    );
  }, RENDER_POLL_INTERVAL_MS);
}
